import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function ReviewsPage() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <section className="pt-20 md:pt-24 pb-8 md:pb-12 bg-background-100">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-2 mb-4">
              <a href="/" className="text-xs text-foreground-500 hover:text-primary-600 transition-colors flex items-center gap-1">
                <i className="ri-home-line w-3 h-3 flex items-center justify-center" /> Home
              </a>
              <span className="text-foreground-300">/</span>
              <span className="text-xs text-primary-600 font-medium">Reviews</span>
            </div>
            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-4 leading-tight">
              CSSBuy <span className="text-primary-500">Review</span>
            </h1>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed max-w-2xl">
              An independent, research-based review of CSSBuy. Learn about features, limitations, pros, cons, and what to know before using the service.
            </p>
          </div>
        </div>
      </section>

      <section className="w-full py-12 md:py-16">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-xl border border-background-200 p-6 md:p-8 lg:p-10">
              <div className="prose prose-sm max-w-none text-foreground-600 leading-relaxed">
                <p className="mb-4">
                  This <strong>CSSBuy review</strong> is based on independent research and publicly available information. We are not affiliated with CSSBuy, and this review aims to provide an objective overview of the service for consumers making informed decisions.
                </p>
                <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mt-8 mb-4">What is CSSBuy?</h2>
                <p className="mb-4">
                  CSSBuy is a buying agent service that helps international customers purchase products from Chinese e-commerce platforms such as Taobao, Weidian, and 1688. The service acts as an intermediary, handling purchases, warehouse storage, quality control, and international shipping.
                </p>
                <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mt-8 mb-4">Key Features</h2>
                <ul className="space-y-2 mb-6 list-disc list-inside">
                  <li><strong>Multi-platform purchasing</strong> — Buy from Taobao, Weidian, 1688, and other platforms.</li>
                  <li><strong>QC photos</strong> — Receive quality control photos before international shipping.</li>
                  <li><strong>Parcel consolidation</strong> — Combine multiple orders into one shipment.</li>
                  <li><strong>Storage service</strong> — Free or low-cost warehouse storage for a limited period.</li>
                  <li><strong>Multiple shipping options</strong> — Choose from various carriers and methods.</li>
                </ul>
                <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mt-8 mb-4">Pros and Cons</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <h4 className="font-semibold text-sm text-green-800 mb-3">Pros</h4>
                    <ul className="space-y-2 text-xs text-green-700">
                      <li className="flex items-start gap-2"><i className="ri-check-line w-4 h-4 flex items-center justify-center text-green-600 shrink-0 mt-0.5" /> Access to Chinese marketplaces</li>
                      <li className="flex items-start gap-2"><i className="ri-check-line w-4 h-4 flex items-center justify-center text-green-600 shrink-0 mt-0.5" /> QC photos before shipping</li>
                      <li className="flex items-start gap-2"><i className="ri-check-line w-4 h-4 flex items-center justify-center text-green-600 shrink-0 mt-0.5" /> Parcel consolidation</li>
                      <li className="flex items-start gap-2"><i className="ri-check-line w-4 h-4 flex items-center justify-center text-green-600 shrink-0 mt-0.5" /> Multiple shipping methods</li>
                    </ul>
                  </div>
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <h4 className="font-semibold text-sm text-red-800 mb-3">Cons</h4>
                    <ul className="space-y-2 text-xs text-red-700">
                      <li className="flex items-start gap-2"><i className="ri-close-line w-4 h-4 flex items-center justify-center text-red-600 shrink-0 mt-0.5" /> Shipping can be expensive</li>
                      <li className="flex items-start gap-2"><i className="ri-close-line w-4 h-4 flex items-center justify-center text-red-600 shrink-0 mt-0.5" /> Potential customs delays</li>
                      <li className="flex items-start gap-2"><i className="ri-close-line w-4 h-4 flex items-center justify-center text-red-600 shrink-0 mt-0.5" /> Communication barriers</li>
                      <li className="flex items-start gap-2"><i className="ri-close-line w-4 h-4 flex items-center justify-center text-red-600 shrink-0 mt-0.5" /> Refund process can be slow</li>
                    </ul>
                  </div>
                </div>
                <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mt-8 mb-4">Is CSSBuy Legit?</h2>
                <p className="mb-4">
                  CSSBuy operates as a legitimate buying agent service. However, as with any international purchasing service, there are risks involved. We recommend starting with small orders, using secure payment methods, and thoroughly reviewing QC photos before approving international shipping.
                </p>
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-6">
                  <p className="text-xs text-amber-700">
                    <strong>Disclaimer:</strong> This is an independent review. CSSBuyBR.info is not affiliated with CSSBuy. Always verify current policies and terms directly with CSSBuy before using their service.
                  </p>
                </div>
                <div className="p-5 bg-primary-50 border border-primary-200 rounded-xl mt-8">
                  <p className="text-sm text-primary-800 font-medium mb-3">Learn more about CSSBuy</p>
                  <a href="/cssbuy-spreadsheet/" className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-white text-sm font-semibold rounded-lg hover:bg-primary-600 transition-colors mr-3">
                    CSSBuy Spreadsheet Guide
                    <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
                  </a>
                  <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-6 py-2.5 bg-white text-primary-600 border border-primary-200 text-sm font-semibold rounded-lg hover:bg-primary-50 transition-colors">
                    Visit Main Site
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}